const DISALLOWED_NAME_TOKENS = new Set([
  "a",
  "an",
  "and",
  "back",
  "blocked",
  "burned",
  "burnout",
  "burnt",
  "busy",
  "challenge",
  "confused",
  "data",
  "debt",
  "fine",
  "frustrated",
  "good",
  "hello",
  "help",
  "hey",
  "hi",
  "i",
  "issue",
  "lost",
  "me",
  "money",
  "my",
  "name",
  "ok",
  "okay",
  "overwhelmed",
  "problem",
  "process",
  "ready",
  "sales",
  "stressed",
  "stuck",
  "swamped",
  "team",
  "tech",
  "time",
  "tired",
  "we",
  "worried",
]);

const UNAMBIGUOUS_NAME_PREFIXES = [
  /^(?:my name is|call me|this is|it is|it's)\s+/i,
];

const AMBIGUOUS_NAME_PREFIXES = [/^(?:i am|i'm|im)\s+/i];

const NAME_TOKEN_PATTERN = /^[A-Za-z][A-Za-z'-]*$/;

const normalizeWhitespace = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

const trimWrappers = (value: string): string =>
  value.replace(/^[`"'([{<\s]+|[`"')\]}>.,!?\s]+$/g, "").trim();

const toDisplayToken = (token: string): string =>
  token
    .split(/([-'])/)
    .map((part) =>
      part === "-" || part === "'"
        ? part
        : `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`,
    )
    .join("");

const normalizeCandidate = (
  candidate: string,
  maxTokens: number,
): string | null => {
  const trimmed = trimWrappers(normalizeWhitespace(candidate));
  if (!trimmed || trimmed.length > 30) return null;

  const tokens = trimmed.split(" ");
  if (tokens.length === 0 || tokens.length > maxTokens) return null;

  const normalizedTokens = tokens.map((token) => token.trim()).filter(Boolean);
  if (normalizedTokens.length === 0 || normalizedTokens.length > maxTokens) {
    return null;
  }

  const hasInvalidToken = normalizedTokens.some(
    (token) =>
      token.length < 2 ||
      !NAME_TOKEN_PATTERN.test(token) ||
      DISALLOWED_NAME_TOKENS.has(token.toLowerCase()),
  );

  if (hasInvalidToken) return null;

  return normalizedTokens.map(toDisplayToken).join(" ");
};

export const extractNameReply = (
  input: string,
  allowPlainReply: boolean,
): string | null => {
  const normalizedInput = normalizeWhitespace(input);
  if (!normalizedInput) return null;

  for (const prefix of UNAMBIGUOUS_NAME_PREFIXES) {
    if (prefix.test(normalizedInput)) {
      return normalizeCandidate(normalizedInput.replace(prefix, ""), 2);
    }
  }

  for (const prefix of AMBIGUOUS_NAME_PREFIXES) {
    if (prefix.test(normalizedInput)) {
      return normalizeCandidate(normalizedInput.replace(prefix, ""), 1);
    }
  }

  if (!allowPlainReply) return null;
  return normalizeCandidate(normalizedInput, 1);
};

export const sanitizeUserName = (
  value: string | null | undefined,
): string | null => {
  if (typeof value !== "string") return null;
  return normalizeCandidate(value, 2);
};
